import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, ArrowRight, CheckCircle, Award, BookOpen,
  MessageCircle, TrendingUp, Star, Heart, Sparkles,
  Play, ChevronDown, Zap, Gift, Globe, Clock,
  School, GraduationCap
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  ScrollReveal,
  StaggerContainer,
  StaggerItem,
  InteractiveCard,
  MagneticButton,
  FloatingElement,
  GSAPCounter,
} from '@/components/ui/OptimizedAnimations';
import {
  CharacterStagger,
  SparklesCore,
  Spotlight,
} from '@/components/ui/AceternityAnimations';

const features = [
  {
    icon: Award,
    title: 'Certified Courses',
    description: 'Earn recognized certifications that boost your career. Courses designed by education experts and industry leaders.',
  },
  {
    icon: Users,
    title: 'Peer Networking',
    description: 'Connect with educators worldwide. Share ideas, collaborate on projects, and grow together as a community.',
  },
  {
    icon: TrendingUp,
    title: 'Career Growth',
    description: 'Unlock new opportunities with skills that matter. Track your progress and showcase your achievements.',
  },
  {
    icon: BookOpen,
    title: 'Content Creation',
    description: 'Create and share your teaching resources. Build your reputation as a thought leader in education.',
  },
  {
    icon: Sparkles,
    title: 'Free Teaching Tools',
    description: 'Access lesson plan generators, quiz builders, and more — completely free. Save hours every week.',
  },
  {
    icon: MessageCircle,
    title: 'Mentorship Programs',
    description: 'Learn from experienced educators or become a mentor yourself. Give back to the teaching community.',
  },
];

const freeTools = [
  { name: 'Lesson Plan Generator', desc: 'AI-powered plans in minutes' },
  { name: 'Quiz Builder', desc: 'Interactive assessments' },
  { name: 'Rubric Creator', desc: 'Fair grading templates' },
  { name: 'Progress Tracker', desc: 'Student analytics' },
];

const stats = [
  { value: '50', suffix: 'K+', label: 'Teachers Joined' },
  { value: '200', suffix: '+', label: 'Free Courses' },
  { value: '15', suffix: '+', label: 'Countries' },
  { value: '4.9', suffix: '/5', label: 'Rating' },
];

const testimonials = [
  {
    quote: "Lynkt transformed my teaching career. The certifications helped me land a position at a top school, and the community keeps me inspired every day.",
    author: "Priya Sharma",
    role: "Mathematics Teacher, Delhi",
    avatar: "PS",
  },
  {
    quote: "The free tools alone save me 5+ hours every week. The lesson plan generator is magic — it understands exactly what I need.",
    author: "Rahul Verma",
    role: "Science Teacher, Mumbai",
    avatar: "RV",
  },
  {
    quote: "I've connected with amazing educators from around the world. The mentorship program paired me with someone who changed how I approach teaching.",
    author: "Anita Desai",
    role: "English Teacher, Bangalore",
    avatar: "AD",
  },
];

const faqs = [
  {
    question: 'Is Lynkt really free?',
    answer: 'Yes! Our free tier gives you access to all teaching tools, community features, and selected courses. Premium plans unlock advanced certifications and exclusive content.',
  },
  {
    question: 'Are the certifications recognized?',
    answer: 'Our certifications are recognized by schools and institutions across India and internationally. Many teachers have used them to advance their careers.',
  },
  {
    question: 'How much time do the free tools save?',
    answer: 'Teachers report saving 5-10 hours per week using our lesson plan generator, quiz builder, and other tools. That\'s time you can spend with students or yourself.',
  },
  {
    question: 'Can I connect with teachers from other countries?',
    answer: 'Absolutely! Lynkt has teachers from 15+ countries. Our community features make it easy to connect, share resources, and collaborate across borders.',
  },
];

const FAQItem = ({ faq, isOpen, onToggle }) => (
  <motion.div className="glass-card overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full p-6 flex items-center justify-between text-left hover:bg-glass-hover transition-colors"
    >
      <span className="text-foreground font-medium pr-4">{faq.question}</span>
      <motion.div
        animate={{ rotate: isOpen ? 180 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <ChevronDown className="w-5 h-5 text-teal flex-shrink-0" />
      </motion.div>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="px-6 pb-6 text-muted-foreground">
            {faq.answer}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const LynktPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="relative bg-void min-h-screen pt-20">
      {/* Hero Section - Warmer, more inviting */}
      <section className="relative py-24 overflow-hidden">
        <Spotlight className="-top-40 left-0 md:left-60 md:-top-20" fill="hsl(166, 100%, 37%)" />

        <div className="absolute inset-0 z-[1]">
          <SparklesCore
            id="lynktSparkles"
            background="transparent"
            minSize={0.4}
            maxSize={1.2}
            particleDensity={40}
            particleColor="#00BFA5"
          />
        </div>

        <FloatingElement duration={6} distance={15} className="absolute top-20 right-10 opacity-20">
          <Heart className="w-32 h-32 text-teal" />
        </FloatingElement>

        <FloatingElement duration={8} distance={20} className="absolute bottom-20 left-10 opacity-10">
          <Star className="w-24 h-24 text-teal" />
        </FloatingElement>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <ScrollReveal delay={0.1}>
              <Link to="/products" className="inline-flex items-center gap-2 text-teal text-sm mb-6 hover:underline">
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back to Products
              </Link>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-teal/20 flex items-center justify-center">
                  <Users className="w-7 h-7 text-teal" />
                </div>
              </div>
            </ScrollReveal>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-foreground mb-4">
              <CharacterStagger
                text="Lynkt"
                delay={0.3}
                staggerDelay={0.05}
              />
            </h1>
            <h2 className="text-2xl sm:text-3xl font-display text-gradient-teal mb-6">
              Where Great Teachers Grow Together
            </h2>

            <ScrollReveal delay={0.6}>
              <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
                Join 50,000+ educators who are learning, connecting, and thriving.
                Free tools, certified courses, and a community that understands you.
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.8}>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <MagneticButton strength={0.2}>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-primary px-8 py-4 text-lg"
                  >
                    <Sparkles className="w-5 h-5" />
                    Start Free Trial
                  </motion.button>
                </MagneticButton>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-secondary px-8 py-4"
                >
                  <Play className="w-5 h-5" />
                  See How It Works
                </motion.button>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={1}>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="w-4 h-4 text-teal" />
                <span>No credit card required</span>
                <span className="mx-2">•</span>
                <CheckCircle className="w-4 h-4 text-teal" />
                <span>Free tools included</span>
                <span className="mx-2">•</span>
                <CheckCircle className="w-4 h-4 text-teal" />
                <span>Cancel anytime</span>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 border-y border-glass-border bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <ScrollReveal key={stat.label} delay={index * 0.1}>
                <div className="text-center">
                  <div className="text-3xl font-display font-bold text-teal">
                    <GSAPCounter value={stat.value} suffix={stat.suffix} duration={2} />
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Free Tools Section - Highlighted */}
      <section className="py-24 bg-void">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-card p-8 md:p-12 border-teal/20 relative overflow-hidden">
            <div className="absolute top-4 right-4 px-4 py-2 rounded-full bg-teal/20 text-teal text-sm font-medium flex items-center gap-2">
              <Gift className="w-4 h-4" />
              100% Free
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <ScrollReveal>
                  <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
                    Save Hours Every Week
                  </span>
                  <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
                    Free Teaching Tools<br />
                    <span className="text-gradient-teal">That Actually Work</span>
                  </h2>
                  <p className="text-muted-foreground mb-8">
                    We built the tools we wished we had as teachers. Lesson planning, quiz creation,
                    student tracking — all powered by AI, all completely free. No catch.
                  </p>
                  <MagneticButton strength={0.2}>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="btn-primary"
                    >
                      Try Free Tools Now
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  </MagneticButton>
                </ScrollReveal>
              </div>

              <ScrollReveal direction="right">
                <div className="grid grid-cols-2 gap-4">
                  {freeTools.map((tool, index) => (
                    <motion.div
                      key={tool.name}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.02, y: -4 }}
                      className="glass-card p-5 text-center"
                    >
                      <div className="w-10 h-10 rounded-xl bg-teal/20 flex items-center justify-center mx-auto mb-3">
                        <Zap className="w-5 h-5 text-teal" />
                      </div>
                      <h4 className="text-sm font-display font-semibold text-foreground mb-1">
                        {tool.name}
                      </h4>
                      <p className="text-xs text-muted-foreground">{tool.desc}</p>
                    </motion.div>
                  ))}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Everything You Need
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-4">
              Grow Your Teaching Career<br />
              <span className="text-gradient-teal">Your Way</span>
            </h2>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.1} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <StaggerItem key={feature.title}>
                <InteractiveCard hoverScale={1.02} hoverY={-6} glowColor="rgba(0,191,165,0.2)">
                  <div className="glass-card p-6 h-full">
                    <div className="w-12 h-12 rounded-xl bg-teal/20 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-teal" />
                    </div>
                    <h3 className="text-display text-lg text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </InteractiveCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Testimonials Section - Warmer tone */}
      <section className="py-24 bg-void">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-16">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Teacher Stories
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-4">
              Hear From Educators<br />
              <span className="text-gradient-teal">Like You</span>
            </h2>
          </ScrollReveal>

          <StaggerContainer staggerDelay={0.12} className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <StaggerItem key={index}>
                <InteractiveCard hoverScale={1.02} hoverY={-6} glowColor="rgba(0,191,165,0.15)">
                  <div className="glass-card p-6 h-full flex flex-col">
                    <div className="flex gap-1 mb-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-teal text-teal" />
                      ))}
                    </div>
                    <p className="text-foreground mb-6 flex-grow text-sm leading-relaxed">
                      &quot;{testimonial.quote}&quot;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-teal/20 flex items-center justify-center">
                        <span className="text-teal font-display font-bold">{testimonial.avatar}</span>
                      </div>
                      <div>
                        <p className="text-sm font-display font-semibold text-foreground">
                          {testimonial.author}
                        </p>
                        <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </InteractiveCard>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-12">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Questions?
            </span>
            <h2 className="text-display text-3xl text-foreground">
              We&apos;ve Got <span className="text-gradient-teal">Answers</span>
            </h2>
          </ScrollReveal>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <FAQItem
                  faq={faq}
                  isOpen={openFaq === index}
                  onToggle={() => setOpenFaq(openFaq === index ? null : index)}
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Warm and inviting */}
      <section className="py-24 bg-void">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <div className="w-20 h-20 rounded-3xl bg-teal/20 flex items-center justify-center mx-auto mb-8">
              <Heart className="w-10 h-10 text-teal" />
            </div>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-6">
              Join a Community That<br />
              <span className="text-gradient-teal">Gets You</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Teaching is hard. But it doesn&apos;t have to be lonely.
              Connect with 50,000+ educators who are growing together.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <MagneticButton strength={0.2}>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-primary px-10 py-5 text-lg"
                >
                  <Sparkles className="w-5 h-5" />
                  Start Free Trial
                </motion.button>
              </MagneticButton>
            </div>
            <p className="text-sm text-muted-foreground mt-6">
              Join free • No credit card • Cancel anytime
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Explore Suite Section */}
      <section className="py-16 bg-void-light/30 border-t border-glass-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center mb-8">
            <h3 className="text-display text-xl text-foreground">
              For Your School? <span className="text-gradient-orange">Explore Our Suite</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              Recommend Tattva to your school and earn referral bonuses
            </p>
          </ScrollReveal>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { name: 'School ERP', icon: School, href: '/products/school-erp', color: 'teal' },
              { name: 'College ERP', icon: GraduationCap, href: '/products/college-erp', color: 'orange' },
              { name: 'LMS', icon: BookOpen, href: '/products/lms', color: 'orange' },
            ].map((product) => (
              <Link key={product.name} to={product.href}>
                <motion.div
                  whileHover={{ scale: 1.02, y: -4 }}
                  className={cn(
                    'glass-card p-6 flex items-center gap-4 cursor-pointer',
                    'border transition-colors duration-300',
                    product.color === 'teal' ? 'hover:border-teal/40' : 'hover:border-orange/40'
                  )}
                >
                  <div className={cn(
                    'w-12 h-12 rounded-xl flex items-center justify-center',
                    product.color === 'teal' ? 'bg-teal/20' : 'bg-orange/20'
                  )}>
                    <product.icon className={cn(
                      'w-6 h-6',
                      product.color === 'teal' ? 'text-teal' : 'text-orange'
                    )} />
                  </div>
                  <div>
                    <h4 className="text-display text-foreground">{product.name}</h4>
                    <p className="text-xs text-muted-foreground">For institutions</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LynktPage;
